import { InvocaPhoneNumber } from "@src/common/InvocaPhoneNumber";
import { ThemedIcon } from "@src/common/ThemedIcon";
import { APP_ROUTES, LAYOUT_CONSTANTS, OPTUM_BH_HOME_URL } from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useCanvasContext } from "@src/context/CanvasContext";
import { useThemeContext } from "@src/context/ThemeContext";
import { useTimerContext } from "@src/context/TimerContext";
import { useMobileHeaderOffset } from "@src/hooks/useMobileHeaderOffset";
import { handlePatientInformationExit } from "@src/hooks/useNavigationBlocker";
import type { ThemeResponsiveStyle } from "@src/interfaces/theme-config.interface";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Header as AbyssHeader } from "@uhg-abyss/web/ui/Header";
import { Heading } from "@uhg-abyss/web/ui/Heading";
import { IconSymbol } from "@uhg-abyss/web/ui/IconSymbol";
import { NavMenu } from "@uhg-abyss/web/ui/NavMenu";
import { Text } from "@uhg-abyss/web/ui/Text";
import { type CSSProperties, type MouseEvent, useState } from "react";

const MobileDropdownMenu = styled("div", {
  backgroundColor: "$app.color.surface.container.secondary",
  position: "absolute",
  zIndex: 1001,
});

const MenuIcon = styled(IconSymbol, {
  justifySelf: "flex-end",
});

const CENTERED_PAGE_SHELL_CSS = {
  width: "100%",
  maxWidth: LAYOUT_CONSTANTS.maxPageWidth,
  margin: "auto",
} as const;

const HeaderWrapper = styled("div", {
  flexDirection: "column",
  display: "flex",
  position: "relative",
  ...CENTERED_PAGE_SHELL_CSS,
});

const DropdownHeadingRow = styled("div", {
  display: "flex",
  paddingBottom: "24px",
});

const DropdownHeadingTextCol = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  justifyContent: "center",
});

const PhoneRow = styled("div", {
  display: "flex",
  gap: "4px",
});

const CloseIconWrapper = styled("div", {
  padding: "0 16px",
});

const DesktopCallRow = styled("div", {
  display: "flex",
  gap: "6px",
});

const MobileMenuButton = styled("button", {
  padding: "5px 0",
  background: "transparent",
  border: "none",
  textAlign: "left",
  cursor: "pointer",
});

const resolveLogoStyle = (
  style: ThemeResponsiveStyle | undefined,
  isMobile: boolean,
): CSSProperties => {
  if (!style) {
    return {};
  }

  const { isMobile: mobileStyle, ...baseStyle } = style;
  return isMobile ? { ...baseStyle, ...mobileStyle } : baseStyle;
};

interface HeaderProps {
  mobileHeaderOffset?: string;
}

export const Header = ({ mobileHeaderOffset: sharedMobileHeaderOffset }: HeaderProps) => {
  const { activeThemeConfig } = useThemeContext();
  const { data } = useApplicationContext();
  const { isTimerRunning, stopTimer } = useTimerContext();
  const headerTitle = activeThemeConfig.assets.header?.title;
  const shouldShowHeaderTitle = !headerTitle?.hidden;
  const headerTitleText = headerTitle?.text || "Optum Behavioral Care";
  const logoAsset = activeThemeConfig.assets.header?.logo;
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const logoStyle = resolveLogoStyle(logoAsset?.style, isMobile);
  const isSmallerTablet = useMediaQuery(MEDIA_QUERIES.smallerTabletMax);
  const isTablet = useMediaQuery(MEDIA_QUERIES.tabletMax);
  const [displayDropdownMenu, setDisplayDropdownMenu] = useState<boolean>(false);
  const isDesktop = !isMobile;
  const currentPath = globalThis.location.pathname;
  const isPatientInformationRoute =
    currentPath === APP_ROUTES.PATIENT_INFORMATION ||
    currentPath.startsWith(`${APP_ROUTES.PATIENT_INFORMATION}/`);

  const handleBrandmarkClick = () => {
    if (!isPatientInformationRoute) {
      return;
    }

    handlePatientInformationExit({
      userUuid: data.userUuid,
      isTimerRunning,
      stopTimer,
      source: "Header.Brandmark.onClick",
      logMessage:
        "User clicked header brandmark on /patient-information. Unfreezing appointment before navigating to home.",
      shouldSetFindProviderRedirect: false,
    });
  };

  const handleHeaderContainerClickCapture = (event: MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement | null;
    const clickedBrandmark = target?.closest('[data-id="obc-header-brandmark"]');

    if (!clickedBrandmark) {
      return;
    }

    handleBrandmarkClick();
  };

  const { isInSalesforce } = useCanvasContext();
  const shouldMeasureMobileHeaderOffset = sharedMobileHeaderOffset === undefined;
  let headerPadding = "24px 70px 0";
  if (isMobile) {
    headerPadding = "16px 16px 0";
  } else if (isSmallerTablet) {
    headerPadding = "24px 24px 0";
  } else if (isTablet) {
    headerPadding = "24px 32px 0";
  }
  const measuredMobileHeaderOffset = useMobileHeaderOffset({
    isMobile,
    isInSalesforce,
    enabled: shouldMeasureMobileHeaderOffset,
  });
  const mobileHeaderOffset = sharedMobileHeaderOffset ?? measuredMobileHeaderOffset;

  let navPadding = "0 70px";
  if (isSmallerTablet) {
    navPadding = "0 24px";
  } else if (isTablet) {
    navPadding = "0 32px";
  }

  const menuItems = [
    {
      title: "Home",
      href: OPTUM_BH_HOME_URL,
      onClick: () => {
        globalThis.location.href = OPTUM_BH_HOME_URL;
      },
    },
  ];

  const DropdownMenuContent = (
    <MobileDropdownMenu
      data-testid="test-id-header-mobile-dropdown"
      css={{
        top: mobileHeaderOffset,
        padding: "24px",
        right: "-16px",
        maxHeight: displayDropdownMenu ? "500px" : "0",
        animation: displayDropdownMenu
          ? "expand 0.2s ease-in-out forwards"
          : "collapse 0.2s ease-in-out forwards", // Apply the accordion-style animation
      }}
    >
      <DropdownHeadingRow data-testid="test-id-header-mobile-dropdown-heading">
        <DropdownHeadingTextCol data-testid="test-id-header-mobile-dropdown-heading-text">
          {shouldShowHeaderTitle && (
            <Heading
              css={{
                "abyss-heading-root": {
                  fontSize: "20.25px",
                  lineHeight: "24px",
                },
              }}
            >
              {headerTitleText}
            </Heading>
          )}
          <PhoneRow>
            <Text color="$app.color.text.primary" css={{ fontSize: "14px", lineHeight: "18px" }}>
              Call to schedule
            </Text>
            <Text
              color="$app.color.text.primary"
              fontWeight="bold"
              css={{ fontSize: "14px", lineHeight: "18px" }}
            >
              <InvocaPhoneNumber />
            </Text>
          </PhoneRow>
        </DropdownHeadingTextCol>
        <CloseIconWrapper>
          <IconSymbol
            icon="close"
            size="24px"
            data-testid="test-id-header-mobile-dropdown-close-button"
            onClick={() => {
              return setDisplayDropdownMenu(!displayDropdownMenu);
            }}
          />
        </CloseIconWrapper>
      </DropdownHeadingRow>
      <div data-testid="test-id-header-mobile-dropdown-content">
        {menuItems.map((menuItem) => {
          const mobileMenuItemId = `obc-header-mobile-dropdown-menu-item-${menuItem.title.replaceAll(/\s+/g, "-").toLowerCase()}`;

          return (
            <MobileMenuButton
              key={menuItem.title}
              type="button"
              onClick={menuItem.onClick}
              data-testid={`test-id-header-mobile-dropdown-menu-item-${menuItem.title.replaceAll(/\s+/g, "-").toLowerCase()}`}
            >
              <Text
                id={mobileMenuItemId}
                data-obc-label={`Header Mobile Dropdown Menu Item ${menuItem.title}`}
                color="$app.color.text.primary"
                fontWeight="bold"
              >
                {menuItem.title}
              </Text>
            </MobileMenuButton>
          );
        })}
      </div>
    </MobileDropdownMenu>
  );

  return (
    <HeaderWrapper>
      <AbyssHeader.Container
        onClickCapture={handleHeaderContainerClickCapture}
        css={{
          "abyss-header-container": {
            maxWidth: LAYOUT_CONSTANTS.maxContentWidth,
            gap: "16px",
          },
          "abyss-page-header-root": {
            zIndex: 100,
          },
          "abyss-header-root": {
            ...CENTERED_PAGE_SHELL_CSS,
            padding: headerPadding,
            ...(isMobile && {
              position: "fixed", // Make the header sticky only on mobile
              top: 0, // Stick to the top of the viewport
              zIndex: 1000, // Ensure it stays above other elements
              borderBottom: "1px solid $app.color.default.border",
            }),
          },
        }}
      >
        <AbyssHeader.Brandmark
          data-id="obc-header-brandmark"
          data-obc-label="Header Brandmark"
          css={{
            "abyss-header-logo-tooltip": {
              "abyss-tooltip-content": {
                display: "none",
              },
              "abyss-tooltip-arrow": {
                display: "none",
              },
            },
          }}
          {...(logoAsset && {
            logo: <img src={logoAsset.src} alt={logoAsset.alt || "Brand Logo"} style={logoStyle} />,
          })}
        />
        {isDesktop ? (
          <div>
            {shouldShowHeaderTitle && (
              <Heading size="sm" css={{ "abyss-heading-root": { lineHeight: "48px" } }}>
                {headerTitleText}
              </Heading>
            )}
            <DesktopCallRow>
              <Text
                fontWeight="regular"
                color="$app.color.header.text"
                css={{ fontSize: "14.7px !important" }}
              >
                Call to schedule
              </Text>
              <ThemedIcon
                themeIconName="phone"
                themedImgProps={{ "aria-hidden": true }}
                iconBrandProps={{
                  icon: "phone",
                  size: "16px",
                }}
              />
              <Text
                fontWeight="bold"
                color="$app.color.header.text"
                css={{ fontSize: "14.59px !important" }}
              >
                <InvocaPhoneNumber />
              </Text>
            </DesktopCallRow>
          </div>
        ) : (
          <AbyssHeader.Actions>
            <MenuIcon
              icon="menu"
              color="$app.color.icon.primary.interactive"
              size="36px"
              data-testid="test-id-header-mobile-menu-button"
              onClick={() => {
                return setDisplayDropdownMenu(!displayDropdownMenu);
              }}
            />
            {displayDropdownMenu && DropdownMenuContent}
          </AbyssHeader.Actions>
        )}
      </AbyssHeader.Container>
      {isDesktop && (
        <AbyssHeader.Navigation
          css={{
            "abyss-header-navigation-root": {
              borderBottom: "1px solid $app.color.default.border",
              ...CENTERED_PAGE_SHELL_CSS,
            },
          }}
        >
          <NavMenu.Root
            css={{
              "abyss-nav-menu-root": {
                backgroundColor: "$app.color.default.background",
                ...CENTERED_PAGE_SHELL_CSS,
                zIndex: "0 !important",
                padding: navPadding,
              },
              "abyss-nav-menu-container": {
                paddingLeft: "0px",
                paddingRight: "0px",
                backgroundColor: "$app.color.default.background",
                maxWidth: LAYOUT_CONSTANTS.maxContentWidth,
              },
            }}
          >
            <NavMenu.List
              css={{
                "abyss-nav-menu-list": {
                  gap: "40px",
                  backgroundColor: "$app.color.default.background",
                },
              }}
            >
              {menuItems.map((menuItem) => {
                const desktopMenuItemId = `obc-header-desktop-navmenu-link-${menuItem.title.replaceAll(/\s+/g, "-").toLowerCase()}`;

                return (
                  <NavMenu.Link
                    key={menuItem.title}
                    onClick={menuItem.onClick}
                    rel="noopener noreferrer"
                    id={desktopMenuItemId}
                    data-testid={`test-id-header-desktop-navmenu-link-${menuItem.title.replaceAll(/\s+/g, "-").toLowerCase()}`}
                    data-obc-label={`Header Desktop NavMenu Link ${menuItem.title}`}
                    selected={currentPath === menuItem.href}
                    css={{
                      "abyss-nav-menu-link": {
                        maxWidth: "max-content",
                        backgroundColor: "$app.color.default.background",
                        padding: "1rem 0",
                        fontWeight: 600,
                        borderBottom: "4px solid transparent", // Transparent border by default
                        transition: "border-bottom 0.2s ease, background-color 0.2s ease", // Add transition for smooth animation
                        textDecoration: "none !important",
                        "&:hover": {
                          "&:hover": {
                            borderBottom: `4px solid $app.color.header.nav.menu.item`, // Add visible border on hover
                            backgroundColor: "transparent",
                          },
                          backgroundColor: "transparent",
                        },
                        "&:active": {
                          borderBottom: `4px solid $app.color.header.nav.menu.item`, // Active route style
                          backgroundColor: "transparent",
                        },
                        ...(currentPath === menuItem.href && {
                          borderBottom: `4px solid $app.color.header.nav.menu.item`, // Style for selected state
                          fontWeight: 700, // Make the selected item bold
                          color: "$app.color.header.nav.menu.item", // Change text color for selected
                          "&:hover": {
                            color: "$app.color.header.nav.menu.item", // Change text color on hover for selected
                          },
                        }),
                      },
                    }}
                  >
                    {menuItem.title}
                  </NavMenu.Link>
                );
              })}
            </NavMenu.List>
          </NavMenu.Root>
        </AbyssHeader.Navigation>
      )}
    </HeaderWrapper>
  );
};
